import React from 'react';
import { Field, FieldInputProps, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import Button from '../Button';
import FormGroup from './FormGroup';
import { Config as FormConfig, FieldState, FormApi, FormState } from 'final-form';
import { FormSchemaEntry } from './index';
import { gikClassPrefix } from '@gik/core/constants';
import classnames from 'classnames';
import { UISize, UIVariant } from '../types';
import {
  validateForm,
  ValidationError,
  ValidationErrors,
  ValidationOptions,
  ValidationValues,
} from '@gik/ui/Form/validation';
import { generateField } from './generator';
import { FormContext } from './context';
import scrollToFirstErrorDecorator from './scrollToFirstErrorDecorator';
import UI from '../UIManager';
import { translationKeys } from './i18n/en';
import i18n from '@gik/i18n';
import noop from '@gik/core/utils/noop';
import { Analytics } from '@gik/core/utils';

type CustomFormConfig = Omit<FormConfig, 'onSubmit' | 'validate'>;

export type FormType = {
  id: string;
  api: React.MutableRefObject<FormApi>;
  formEl: HTMLFormElement;
  submitAttempt: (ev: React.FormEvent, options: IFormSubmitAttemptOptions) => Promise<boolean>;
  resetForm: () => void;
};

export interface FormProps extends CustomFormConfig {
  id?: string;
  className?: string;

  /**
   * A form schema that specifies what form fields to render
   */
  schema?: FormSchemaEntry[];

  /**
   * A render function can be provided that will render the form fields.
   * If this function is used then the form will not be auto generated from the schema.
   */
  render?: (props: FormRenderProps<object>) => React.ReactNode;

  // TODO: add a cancelButton boolean (same as resetButton and submitButton)

  /**
   * if true a generic reset button will be rendered at the bottom of the form
   */
  resetButton?: boolean;
  resetButtonText?: string;
  /**
   * if true a generic submit button will be rendered at the bottom of the form
   */
  submitButton?: boolean;
  submitButtonText?: string;

  /**
   * This property is passed to the FormGroup component to display a different layout
   */
  vertical?: boolean;

  /**
   * This property is passed to the form component to disable them
   */
  disabled?: boolean;

  /**
   * A custom renderField function can be provided for edge cases
   */
  renderField?(
    entry: FormSchemaEntry,
    props: FieldInputProps<unknown, HTMLElement>,
    fieldState: FieldState<unknown>
  ): React.ReactElement;

  /**
   * Callback function that will get triggered when the form is successfully submitted
   */
  onSubmit?: (values: object, form: FormApi) => void | Promise<void>;

  /**
   * Callback function that will get triggered when a from value changes
   */
  onChange?: (values: object) => void | Promise<void>;

  validate?: (values: object, options: ValidationOptions) => ValidationErrors | Promise<ValidationErrors>;

  /**
   * Variant to pass on to all control components
   */
  variant?: UIVariant;

  /**
   * Variant to pass on to all  control components
   */
  size?: UISize;

  /**
   * Wether or not to open a notification when a validation error occurs while trying to submit
   */
  validationNotifications?: boolean;

  /**
   * Wether or not to show inline form errors below each field
   */
  inlineErrors?: boolean;

  /**
   * Tracks error messages
   */
  trackingId?: string;

  /**
   * Disable scroll to first invalid field behavior.
   */
  disableScrollToFirstError?: boolean;

  onReady?: (form: FormApi) => void;
}

export interface IFormSubmitAttemptOptions {
  useValidationNotifications?: boolean;
  notificationError?: string;
  formRenderProps?: FormRenderProps;
}

// block name for this component
const blockName = `${gikClassPrefix}-form`;

const scrollOnErrors = scrollToFirstErrorDecorator();

/**
 * GIK Form
 *
 * This is a wrapper around react-final-form.
 * It hooks up a schema with the validation method and it also
 * provides a context that each field has access to.
 *
 * It can also auto generate the form fields based on the schema.
 */
function Form(
  {
    schema,
    initialValues = {},
    onSubmit,
    onChange,
    onReady = noop,
    render,
    validate,
    vertical,
    disabled,
    resetButton,
    resetButtonText = 'Reset',
    submitButton,
    submitButtonText = 'Submit',
    variant,
    size,
    id,
    validationNotifications = true,
    inlineErrors = true,
    className,
    trackingId,
    disableScrollToFirstError = false,
    ...otherProps
  }: FormProps,
  ref
): React.ReactElement {
  const [_initialValues, _setInitialValues] = React.useState(undefined);
  const [values, _setValues] = React.useState(undefined);
  const [ready, setReady] = React.useState(false);

  let formRef: HTMLFormElement;

  const valuesRef = React.useRef(values);
  const setValues = data => {
    valuesRef.current = data;
    // the if statement prevent a state update if the component was unmounted
    if (formRef) {
      _setValues(data);
    }
  };

  const formApiRef = React.useRef<FormApi>(null);

  const formRenderProps = React.useRef<FormRenderProps>();

  React.useImperativeHandle(ref, () => ({
    id,
    formEl: formRef,
    api: formApiRef,
    submitAttempt: handleSubmitAttempt,
    resetForm: () => handleReset(formApiRef.current),
  }));

  React.useEffect(() => {
    const newInitialValues = initialValues;

    if (schema) {
      // assign initial values from the 'default' variable in the schema property
      schema.forEach(entry => {
        if (newInitialValues[entry.name] === undefined) {
          newInitialValues[entry.name] = entry.default;
        }
      });
    }
    _setInitialValues(newInitialValues);
    // FIXME: depends on initialValues but adding initialValues causes an infinite render loop
    // eslint-disable-next-line
  }, []);

  function renderFormGroup(entry: FormSchemaEntry, key: string | number, schemaComponentProps: object) {
    if (!entry) {
      return null;
    }
    return (
      <Field type={entry.type as string} key={key} name={entry.name}>
        {({ input, meta: fieldState }) => {
          return (
            <FormGroup
              fieldState={fieldState as FieldState<object>}
              id={entry.name}
              fieldWidth={entry.width}
              fieldType={entry.type || 'plaintext'}
              vertical={vertical}
              showError={inlineErrors}
              key={key}
              label={entry.label}
              {...schemaComponentProps}
            >
              {generateField({ entry, formFieldProps: input, schemaComponentProps, fieldState })}
            </FormGroup>
          );
        }}
      </Field>
    );
  }

  function handleOnSubmit(values: object, form: FormApi): void | Promise<void> {
    // prevent submitting form while it is already being submitted
    if (formRenderProps.current.submitting) {
      return;
    }

    if (onSubmit) {
      return onSubmit(values, form);
    }
  }

  function handleReset(form: FormApi): void {
    // hard reset the form to clear validation errors
    if (schema) {
      schema.forEach(entry => {
        form.change(entry.name, undefined);
        form.resetFieldState(entry.name);
      });
    }

    // reset react final form to initial values
    form.reset();
  }

  function handleChange(formState: FormState<object>) {
    if (onChange) {
      onChange(formState.values);
    }
    setValues(formState.values);
    if (!ready) {
      setReady(true);
      onReady(formApiRef.current);
    }
  }

  function handleValidateForm(values: ValidationValues) {
    const opts = {
      formEl: formRef,
      formRenderProps: formRenderProps.current,
    };
    return validate ? validate(values, opts) : validateForm(schema, values, opts);
  }

  async function handleSubmitAttempt(
    ev: React.FormEvent = null,
    options?: IFormSubmitAttemptOptions
  ): Promise<boolean> {
    if (ev) {
      ev.preventDefault();
    }

    const renderProps = options?.formRenderProps;

    if (validationNotifications || options?.useValidationNotifications) {
      // trigger form validation to see if there are any errors at this point
      // Note: Use valuesRef as handleSubmitAttempt can be called from outside of this component (different context)
      const errors = handleValidateForm(valuesRef.current);

      const errorKeys = Object.keys(errors);
      if (errorKeys.length) {
        let errorNames = '';
        let singleError: ValidationError;
        const matchingErrors: ValidationError[] = [];
        errorKeys.forEach((key, index) => {
          let separatorSymbol = '';
          if (index == errorKeys.length - 1 && matchingErrors.length > 0) {
            separatorSymbol = ' and ';
          } else if (index > 0) {
            separatorSymbol = ', ';
          }

          const validationError = errors[key] as ValidationError;
          const schemaEntry = schema?.find(entry => entry.name === key);

          // fire analytics events
          if (trackingId) {
            try {
              Analytics.fireEvent(
                trackingId,
                {
                  ValidationError: `${schemaEntry?.name || key}:${
                    schemaEntry?.type === 'password' ? '*** sensitive info removed ***' : values?.[key]
                  }:${errors?.[key].message}`,
                },
                keys => keys
              );
            } catch (e) {
              console.error(
                e,
                'trackingId',
                trackingId,
                'schemaEntry',
                schemaEntry,
                'values',
                values,
                'errors',
                errors,
                'key',
                key
              );
            }
          }

          if (schemaEntry) {
            errorNames += `${separatorSymbol}${
              schemaEntry.errorName || schemaEntry.label || schemaEntry.placeholder || schemaEntry.name
            }`;

            // only add fields that failed to pass the 'required' validation
            if (validationError.type === 'required') {
              // add field name from the schema
              matchingErrors.push(validationError);
            }
            if (validationError.type === 'singleRequired') {
              // add field name from the schema
              singleError = validationError;
            }
          }
        });

        // just fire a custom notification if one was supplied
        if (options?.notificationError) {
          UI.notifyError(options.notificationError);
        } else {
          if (singleError) {
            UI.notifyError(i18n.t(singleError.message));
          } else {
            if (matchingErrors.length > 10 || matchingErrors.length === 0) {
              UI.notifyError(
                i18n.t(
                  matchingErrors.some(error => error.type === 'required' || error.type === 'singleRequired')
                    ? translationKeys.genericFormErrorFieldRequired
                    : translationKeys.genericFormErrorFieldError
                )
              );
            } else {
              if (matchingErrors.length === 1) {
                UI.notifyError(`The ${errorNames} field is required.`);
              } else {
                UI.notifyError(`The ${errorNames} fields are required.`);
              }
            }
          }
        }
      }
    }

    // allow submit attempt to go through (which will perform another round of validations)
    const result = renderProps ? renderProps?.handleSubmit() : await formApiRef.current.submit();
    // TODO: maybe we should show errors here
    return !result;
  }

  // wait until initialValues is ready
  if (!_initialValues) {
    return null;
  }

  const blockClasses = classnames([
    blockName,
    `${blockName}--generated`,
    { [`${blockName}--vertical`]: vertical },
    className || '',
  ]);

  return (
    <FormContext.Provider
      initialState={{
        schema,
        vertical,
      }}
    >
      <FinalForm
        onSubmit={handleOnSubmit}
        decorators={[...(!disableScrollToFirstError ? [scrollOnErrors] : [])]}
        initialValues={_initialValues}
        validate={handleValidateForm}
        {...otherProps}
        render={(props: FormRenderProps) => {
          formApiRef.current = props.form;
          formRenderProps.current = props;

          return (
            <>
              <FormSpy onChange={handleChange} subscription={{ values: true }} />

              <form
                ref={(_ref: HTMLFormElement) => {
                  formRef = _ref;
                  return ref;
                }}
                onSubmit={ev => handleSubmitAttempt(ev, { formRenderProps: props })}
                id={id}
                className={blockClasses}
                noValidate
              >
                {!render &&
                  schema.map((entry: FormSchemaEntry, index: number) => {
                    return renderFormGroup(entry, index, { variant, size });
                  })}

                {render && render(props)}

                {(resetButton || submitButton) && (
                  <div className="gik-form__actions">
                    {resetButton && (
                      <Button
                        type="reset"
                        onClick={() => handleReset(props.form)}
                        disabled={disabled || props.submitting || props.pristine}
                      >
                        {resetButtonText}
                      </Button>
                    )}
                    {submitButton && (
                      <Button
                        variant={variant}
                        type="submit"
                        loading={props.submitting}
                        disabled={disabled || props.submitting}
                      >
                        {submitButtonText}
                      </Button>
                    )}
                  </div>
                )}
              </form>
            </>
          );
        }}
      />
    </FormContext.Provider>
  );
}

export default React.forwardRef<FormType, FormProps>(Form);

export const FormNoRef = Form;
